import { base64Encode } from "@openanalyst-ai/core/util/encode"

export type PermissionMode = "default" | "auto-review" | "full-access"

export function acceptKey(sessionID: string, directory?: string) {
  if (!directory) return sessionID
  return `${base64Encode(directory)}/${sessionID}`
}

export function directoryAcceptKey(directory: string) {
  return `${base64Encode(directory)}/*`
}

function accepted(autoAccept: Record<string, boolean>, sessionID: string, directory?: string) {
  const key = acceptKey(sessionID, directory)
  const directoryKey = directory ? directoryAcceptKey(directory) : undefined
  return autoAccept[key] ?? autoAccept[sessionID] ?? (directoryKey ? autoAccept[directoryKey] : undefined)
}

function acceptedMode(
  modes: Record<string, PermissionMode>,
  autoAccept: Record<string, boolean>,
  sessionID: string,
  directory?: string,
) {
  const key = acceptKey(sessionID, directory)
  const directoryKey = directory ? directoryAcceptKey(directory) : undefined
  const mode = modes[key] ?? modes[sessionID] ?? (directoryKey ? modes[directoryKey] : undefined)
  if (mode) return mode

  const value = accepted(autoAccept, sessionID, directory)
  if (value === undefined) return undefined
  return value ? "full-access" : "default"
}

export function isDirectoryAutoAccepting(autoAccept: Record<string, boolean>, directory: string) {
  const key = directoryAcceptKey(directory)
  return autoAccept[key] ?? false
}

export function permissionModeAutoAccepts(mode: PermissionMode) {
  return mode === "full-access"
}

// Permission types considered "high risk" — they execute commands or touch
// resources outside the project root, or read private account/billing data.
// Even on "auto-review" mode these always pop the consent prompt; only "full-access" auto-accepts them.
const HIGH_RISK_PERMISSIONS = new Set(["bash", "external_directory", "account_billing"])
const ALWAYS_PROMPT_PERMISSIONS = new Set(["shell_risk"])

// Safety-brake / sensitive permissions that must NEVER be converted into a
// persistent "always" allow by the full-access auto-responder. doom_loop is
// the runaway-tool-call brake — learning it would permanently disable the
// brake for the whole project; external_directory guards writes outside the
// workspace; account_billing reads private billing data. Under full access
// these still auto-accept, but only for the single request ("once"). A user
// can still persist them deliberately via the explicit "Allow Always" button.
export const NEVER_LEARN_PERMISSIONS = new Set(["doom_loop", "external_directory", "account_billing"])

// Decide whether the active mode should auto-accept a specific request.
// - full-access  → accept everything (bash included)
// - auto-review  → accept low-risk only (read/edit/grep/lsp/webfetch/…),
//                  pop the prompt for bash + external_directory
// - default      → accept nothing, always prompt
export function permissionModeAutoAcceptsRequest(mode: PermissionMode, permission?: string, force?: boolean) {
  if (force || (permission && ALWAYS_PROMPT_PERMISSIONS.has(permission))) return false
  if (mode === "full-access") return true
  if (mode === "auto-review") {
    if (!permission) return false
    return !HIGH_RISK_PERMISSIONS.has(permission)
  }
  return false
}

export function directoryPermissionMode(
  modes: Record<string, PermissionMode>,
  autoAccept: Record<string, boolean>,
  directory: string,
) {
  const key = directoryAcceptKey(directory)
  return modes[key] ?? (autoAccept[key] ? "full-access" : "default")
}

export function permissionModeStoreUpdate(mode: PermissionMode, sessionID: string | undefined, directory: string) {
  const directoryKey = directoryAcceptKey(directory)
  const autoAccept = permissionModeAutoAccepts(mode)
  const sessionKey = sessionID ? acceptKey(sessionID, directory) : undefined

  return {
    mode: {
      [directoryKey]: mode,
      ...(sessionKey ? { [sessionKey]: mode } : {}),
    },
    autoAccept: {
      [directoryKey]: autoAccept,
      ...(sessionKey ? { [sessionKey]: autoAccept } : {}),
    },
  }
}

function sessionLineage(session: { id: string; parentID?: string }[], sessionID: string) {
  const parent = session.reduce((acc, item) => {
    if (item.parentID) acc.set(item.id, item.parentID)
    return acc
  }, new Map<string, string>())
  const seen = new Set([sessionID])
  const ids = [sessionID]

  for (const id of ids) {
    const parentID = parent.get(id)
    if (!parentID || seen.has(parentID)) continue
    seen.add(parentID)
    ids.push(parentID)
  }

  return ids
}

export function sessionPermissionMode(
  modes: Record<string, PermissionMode>,
  autoAccept: Record<string, boolean>,
  session: { id: string; parentID?: string }[],
  sessionID: string,
  directory?: string,
) {
  const value = sessionLineage(session, sessionID)
    .map((id) => acceptedMode(modes, autoAccept, id, directory))
    .find((item): item is PermissionMode => item !== undefined)
  return value ?? "default"
}

export function autoRespondsPermission(
  autoAccept: Record<string, boolean>,
  session: { id: string; parentID?: string }[],
  permission: { sessionID: string },
  directory?: string,
) {
  const value = sessionLineage(session, permission.sessionID)
    .map((id) => accepted(autoAccept, id, directory))
    .find((item): item is boolean => item !== undefined)
  return value ?? false
}
